'use client'

import * as React from 'react'
import { cn } from '../../../lib/utils'
import Icon, { icons, type iconTypes } from '../Icon'

export type OptionTagProps = {
  label: string
  color?: string | null
  /** Lucide icon name. Takes the place of the color dot; unknown names fall back to it. */
  icon?: iconTypes | null
  size?: 'sm' | 'md'
  title?: string
  className?: string
  /** When set, the tag shows a remove button. */
  handleRemove?: () => void
}

/**
 * Colored pill for a field option. Colors are mixed against the theme so any
 * user-picked option color stays readable in light and dark mode.
 */
export const OptionTag = ({
  label,
  color,
  icon,
  size = 'md',
  title,
  className,
  handleRemove,
}: OptionTagProps) => (
  <span
    className={cn(
      'inline-flex min-w-0 max-w-full items-center rounded-full font-medium leading-none',
      size === 'sm'
        ? 'h-[18px] gap-1 px-1.5 text-[11px]'
        : 'h-[22px] gap-1.5 px-2 text-xs',
      handleRemove && (size === 'sm' ? 'pr-0.5' : 'pr-1'),
      !color && 'bg-muted text-muted-foreground',
      className,
    )}
    style={
      color
        ? {
            color: `color-mix(in srgb, ${color} 55%, hsl(var(--foreground)))`,
            backgroundColor: `color-mix(in srgb, ${color} 14%, transparent)`,
            boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${color} 22%, transparent)`,
          }
        : undefined
    }
    title={title ?? label}
  >
    {icon && icons[icon] ? (
      <Icon name={icon} size={size === 'sm' ? 11 : 12} className="shrink-0" />
    ) : (
      color && (
        <span
          aria-hidden
          className={cn(
            'shrink-0 rounded-full',
            size === 'sm' ? 'h-1.5 w-1.5' : 'h-2 w-2',
          )}
          style={{ backgroundColor: color }}
        />
      )
    )}
    <span className="truncate">{label}</span>
    {handleRemove && (
      <button
        type="button"
        aria-label={`Remove ${label}`}
        className="flex shrink-0 cursor-pointer items-center justify-center rounded-full p-0.5 opacity-60 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none"
        onClick={(event) => {
          event.stopPropagation()
          handleRemove()
        }}
      >
        <Icon name="X" size={size === 'sm' ? 10 : 12} />
      </button>
    )}
  </span>
)
